import React, { useEffect } from 'react';

import { ROUTES } from '@elara/client-core/constants';

import { useNavigate, useQueryParams, useSessionState } from '#states/hooks.js';
import { Alert, Button } from '#ui/index.js';
import { SessionLayout } from '#views/layout/SessionLayout.jsx';
import { LinkButton } from '#views/Router.jsx';

import { ResetPasswordPage } from './ResetPasswordPage.jsx';

export function FirebaseActionPage() {
  const navigate = useNavigate();
  const query = useQueryParams();

  const mode = query.mode;
  const oobCode = query.oobCode;

  // Handle missing or unsupported mode
  if (!mode) {
    return (
      <SessionLayout title="Link not supported" footer={undefined}>
        <div className="flex flex-col">
          <LinkButton to={ROUTES.LOGIN} size="lg">
            Sign in
          </LinkButton>
        </div>
      </SessionLayout>
    );
  }

  if (mode === 'resetPassword') {
    // Delegate to the dedicated reset password page which reads oobCode from the query string.
    return <ResetPasswordPage />;
  }

  if (!oobCode) {
    return (
      <SessionLayout title="Invalid link" footer={undefined}>
        <div className="space-y-6">
          <Alert variant="destructive">This link is missing the required code. Please request a new one.</Alert>
          <div className="flex flex-col gap-3">
            <LinkButton to={ROUTES.LOGIN} size="lg">
              Sign in
            </LinkButton>
          </div>
        </div>
      </SessionLayout>
    );
  }

  if (mode === 'verifyEmail' || mode === 'verifyAndChangeEmail' || mode === 'verifyBeforeChangeEmail') {
    return (
      <VerifyEmailAction oobCode={oobCode} onComplete={() => navigate.navigate(ROUTES.LOGIN, { replace: true })} />
    );
  }

  if (mode === 'recoverEmail') {
    return (
      <RecoverEmailAction
        oobCode={oobCode}
        onComplete={() => {
          navigate.navigate(ROUTES.LOGIN, { replace: true });
        }}
      />
    );
  }

  // Fallback for unsupported modes
  return (
    <SessionLayout title="Link not supported" footer={undefined}>
      <div className="space-y-6">
        <Alert variant="destructive">This link type is not supported in the app.</Alert>
        <div className="flex flex-col gap-3">
          <LinkButton to={ROUTES.LOGIN} size="lg">
            Sign in
          </LinkButton>
        </div>
      </div>
    </SessionLayout>
  );
}

function VerifyEmailAction({ oobCode, onComplete }: { oobCode: string; onComplete: () => void }) {
  const sessionState = useSessionState();
  const [state, setState] = React.useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = React.useState<string | undefined>();

  useEffect(() => {
    sessionState
      .applyActionCode(oobCode)
      .then(() => {
        setState('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to verify email');
        setState('error');
      });
  }, [oobCode, sessionState]);

  const footer = (
    <p>
      <Button variant="outline" size="sm" onClick={onComplete}>
        Continue to sign in
      </Button>
    </p>
  );

  if (state === 'verifying') {
    return (
      <SessionLayout title="Verifying your email" footer={footer}>
        <p>One moment while we verify your email address…</p>
      </SessionLayout>
    );
  }

  if (state === 'error') {
    return (
      <SessionLayout title="Email verification failed" footer={footer}>
        <Alert variant="destructive">{error}</Alert>
      </SessionLayout>
    );
  }

  return (
    <SessionLayout title="Email verified" footer={footer}>
      <p>Your email has been verified. You can now sign in.</p>
    </SessionLayout>
  );
}

function RecoverEmailAction({ oobCode, onComplete }: { oobCode: string; onComplete: () => void }) {
  const sessionState = useSessionState();
  const [state, setState] = React.useState<'applying' | 'success' | 'error'>('applying');
  const [error, setError] = React.useState<string | undefined>();

  useEffect(() => {
    sessionState
      .applyActionCode(oobCode)
      .then(() => {
        setState('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to restore your email');
        setState('error');
      });
  }, [oobCode, sessionState]);

  const footer = (
    <p>
      <Button variant="outline" size="sm" onClick={onComplete}>
        Continue to sign in
      </Button>
    </p>
  );

  if (state === 'applying') {
    return (
      <SessionLayout title="Restoring your email" footer={footer}>
        <p>One moment while we restore your email address…</p>
      </SessionLayout>
    );
  }

  if (state === 'error') {
    return (
      <SessionLayout title="Email restore failed" footer={footer}>
        <Alert variant="destructive">{error}</Alert>
      </SessionLayout>
    );
  }

  return (
    <SessionLayout title="Email restored" footer={footer}>
      <p>Your email address has been restored. You can now sign in.</p>
    </SessionLayout>
  );
}
